import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import type { CustomerFragment } from "@/v2-backoffice-client/customer/__types__/customer.fragment"
import { useCustomerEditMutation } from "@/v2-backoffice-client/customer/edit/customer.edit.mutation"
import CustomerEditForm from "@/v2-backoffice-client/customer/edit/CustomerEditForm"

export type CustomerEditDialogProps = DialogProps & {
  customer: CustomerFragment | null | undefined
}

function CustomerEditDialog(props: CustomerEditDialogProps) {
  const { customer, open, onOpenChange, ...dialogProps } = props

  const [updateCustomer, { loading, error }] = useCustomerEditMutation({
    onCompleted(data) {
      const isLinked = !!data.updateCustomer.Company
      toast({
        variant: "success",
        title: "Customer updated",
        description: isLinked
          ? "The new identity values were pushed to the linked HubSpot company (app-wins)."
          : "Saved. The customer has no linked HubSpot company, so nothing was pushed.",
      })
      onOpenChange?.(false)
    },
  })

  if (!customer) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange} {...dialogProps}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>Update Customer</Dialog.Title>
          <Dialog.Description>
            Identity fields are app-owned: edits overwrite the linked HubSpot
            company on save. CRM fields (license, stage, owner…) stay read-only
            here and live in HubSpot.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <CustomerEditForm
          id="customer-edit-form"
          customer={customer}
          onSubmit={(data, e) => {
            e.preventDefault()
            updateCustomer({
              variables: {
                id: customer.Id,
                input: data,
              },
            })
          }}
        />

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange?.(false)
            }}
          >
            Cancel
          </Button>

          <Button type="submit" isLoading={loading} form="customer-edit-form">
            {loading ? "Saving..." : "Save"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default CustomerEditDialog
